import {
	getDepositMeta,
	isDepositEnabled,
	type DepositConfig,
	type DepositParametricSource
} from "~/utils/cart/deposit";

/**
 * The deployment's container-deposit config. Centralised here because the
 * generated runtime-config type bakes literal keys from whatever env value was
 * set at `nuxi prepare` time, so every read needs the same cast.
 */
export const useDepositConfig = (): DepositConfig => {
	const deposit = useRuntimeConfig().public?.norce?.deposit as
		| Partial<DepositConfig>
		| undefined;
	return {
		parametricPartNoMap:
			(deposit?.parametricPartNoMap as Record<string, string>) ?? {},
		unitsParametricId: Number(deposit?.unitsParametricId ?? 0)
	};
};

/**
 * Reactive "does this product carry a deposit" — for badges and labels.
 * Pass the variant first and the parent product as fallback.
 */
export const useHasDeposit = (
	sources: () => (DepositParametricSource | undefined | null)[]
) => {
	const config = useDepositConfig();
	return computed(
		() => isDepositEnabled(config) && getDepositMeta(sources(), config) !== null
	);
};

/**
 * Builds the deposit child line (`{PartNo, Quantity}`) for a product being
 * added to the cart, or undefined when the product has no deposit.
 *
 * Listing items (product cards / quick buy) carry the deposit flag in their
 * parametric seeds but NOT the units value, so for those the full product
 * is fetched once to get the real per-unit count (e.g. 12 for a 12-pack).
 */
export const useDepositChild = () => {
	const config = useDepositConfig();

	const buildDepositChild = async (
		product: (DepositParametricSource & { UniqueName?: string }) | undefined,
		quantity: number,
		parent?: DepositParametricSource | null
	): Promise<{ PartNo: string; Quantity: number } | undefined> => {
		if (!product || !isDepositEnabled(config)) return undefined;

		let meta = getDepositMeta([product, parent], config);
		if (!meta) return undefined;

		const hasFullParametrics = [product, parent].some((source) =>
			Array.isArray(source?.Parametrics)
		);
		if (!hasFullParametrics && product.UniqueName) {
			try {
				const { cultureCode, localeCode } = useNorceStore().storeContext;
				const full = await useNorcePwa().getProductByUniqueName({
					uniqueName: product.UniqueName,
					cultureCode,
					localeCode
				});
				meta = getDepositMeta([full], config) ?? meta;
			} catch (error) {
				// Keep the listing-derived meta (perUnit 1) rather than
				// dropping the deposit entirely — but leave a trace, since
				// this undercharges multi-packs until the next add.
				console.error(
					`[deposit] failed to resolve units for ${product.UniqueName}, falling back to perUnit=1`,
					error
				);
			}
		}

		return { PartNo: meta.depositPartNo, Quantity: quantity * meta.perUnit };
	};

	return { buildDepositChild };
};
